import { useState, useRef, useEffect } from 'react';
import './Chatbot.css';

/* ============================================
   KNOWLEDGE BASE
   ============================================ */
const knowledgeBase = [
  {
    keywords: ['program', 'programs', 'offer', 'service', 'services', 'provide', 'help', 'support'],
    answer: 'We provide comprehensive support through six core programs:\n\n- Housing & Shelter\n- Education & Literacy\n- Financial Literacy\n- Tech Education & Literacy\n- Meals & Nutrition\n- Advocacy & Support Services\n\nEach program is designed to address the unique needs of veterans and survivors in our community.',
  },
  {
    keywords: ['veteran', 'veterans', 'military', 'served', 'service', 'army', 'navy', 'marine', 'air force'],
    answer: 'Mission Critical Support Group is dedicated to honoring and supporting U.S. military veterans, including those facing homelessness. We provide essential resources, education, meals, tech education, financial literacy, housing, and pathways to renewed dignity and stability.',
  },
  {
    keywords: ['domestic', 'violence', 'abuse', 'trafficking', 'women', 'children', 'survivor', 'survivors', 'shelter', 'safe'],
    answer: 'We are equally committed to protecting and empowering women and children impacted by domestic violence, abuse, or trafficking through safe shelter, advocacy, and life-changing support services. If you or someone you know needs immediate help, please contact the National Domestic Violence Hotline at 1-800-799-7233.',
  },
  {
    keywords: ['location', 'address', 'where', 'katy', 'houston', 'directions', 'find', 'visit', 'located', 'fulshear', 'cardiff', 'fort bend'],
    answer: "We're located at 4031 FM 1463 RD STE 40, Katy, TX 77494, in the Cardiff neighborhood of Fort Bend County. We serve the greater Houston area including Katy, Fulshear, Harris County, and Fort Bend County.",
  },
  {
    keywords: ['donate', 'donation', 'give', 'money', 'contribute', 'fund', 'sponsor', 'support financially'],
    answer: "Thank you for your generosity! You can support us by visiting our /donate page. All donations are tax-deductible as we are a registered 501(c)(3) organization (EIN: 39-3059429). Every dollar goes directly toward supporting veterans and survivors.",
  },
  {
    keywords: ['volunteer', 'volunteering', 'help out', 'get involved', 'join'],
    answer: "We welcome volunteers! There are many ways to get involved:\n\n- Meal preparation and distribution\n- Mentoring and tutoring\n- Administrative support\n- Event coordination\n- Community outreach\n\nContact us at contact@mcsg.org to learn about current opportunities!",
  },
  {
    keywords: ['nonprofit', '501c3', '501', 'tax', 'deductible', 'ein', 'charity', 'organization', 'registered'],
    answer: "Yes! Mission Critical Support Group is a registered 501(c)(3) nonprofit organization (EIN: 39-3059429). All donations are tax-deductible to the extent allowed by law.",
  },
  {
    keywords: ['contact', 'email', 'phone', 'reach', 'touch', 'message', 'talk'],
    answer: "You can reach us at:\n\nEmail: contact@mcsg.org\nAddress: 4031 FM 1463 RD STE 40, Katy, TX 77494\n\nYou can also fill out the contact form on our homepage!",
  },
  {
    keywords: ['housing', 'home', 'homeless', 'shelter', 'live', 'living'],
    answer: "Our Housing & Shelter program provides safe, stable housing for veterans facing homelessness and women and children escaping domestic violence. We work with community partners to connect individuals with transitional and permanent housing options.",
  },
  {
    keywords: ['education', 'literacy', 'learn', 'school', 'training', 'class'],
    answer: "We offer Education & Literacy programs to help individuals build knowledge and confidence. Our Tech Education program bridges the digital divide with computer skills and software training for career readiness.",
  },
  {
    keywords: ['financial', 'money', 'budget', 'credit', 'savings', 'finance'],
    answer: "Our Financial Literacy program teaches budgeting, savings, credit management, and financial planning. These skills foster long-term independence and stability for veterans and survivors.",
  },
  {
    keywords: ['meal', 'meals', 'food', 'nutrition', 'eat', 'hungry', 'hunger'],
    answer: "Our Meals & Nutrition program provides nutritious meals and food assistance to veterans, families, and individuals in need throughout our community. No one should go hungry while rebuilding their life.",
  },
  {
    keywords: ['hello', 'hi', 'hey', 'sup', 'greetings', 'good morning', 'good afternoon'],
    answer: "Hello! Welcome to Mission Critical Support Group! I'm here to help answer your questions about our programs, location, donations, volunteering, and more. What would you like to know?",
  },
  {
    keywords: ['thank', 'thanks', 'appreciate', 'awesome', 'great', 'cool'],
    answer: "You're welcome! Is there anything else I can help you with? Feel free to ask about our programs, location, donation options, or anything else!",
  },
];

/* ============================================
   KEYWORD MATCHER
   ============================================ */
function findBestMatch(input) {
  const words = input.toLowerCase().replace(/[^\w\s]/g, '').split(/\s+/);
  let bestMatch = null;
  let bestScore = 0;

  for (const entry of knowledgeBase) {
    let score = 0;
    for (const word of words) {
      for (const keyword of entry.keywords) {
        if (word === keyword) score += 3;
        else if (keyword.includes(word) || word.includes(keyword)) score += 1;
      }
    }
    if (score > bestScore) {
      bestScore = score;
      bestMatch = entry;
    }
  }

  return bestScore >= 2 ? bestMatch.answer : null;
}

/* ============================================
   ICONS
   ============================================ */
const ChatIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 6 18" /><path d="m6 6 12 12" />
  </svg>
);

const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m22 2-7 20-4-9-9-4z" /><path d="M22 2 11 13" />
  </svg>
);

const quickReplies = [
  'What programs do you offer?',
  'Where are you located?',
  'How can I donate?',
  'How do I volunteer?',
];

/* ============================================
   CHATBOT COMPONENT
   ============================================ */
function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); };

  useEffect(() => { scrollToBottom(); }, [messages, isTyping]);
  useEffect(() => { if (isOpen && inputRef.current) inputRef.current.focus(); }, [isOpen]);

  const toggleChat = () => {
    if (!isOpen && !hasOpened) {
      setHasOpened(true);
      setMessages([{ from: 'bot', text: "Hello! Welcome to Mission Critical Support Group. I'm here to help answer your questions about our programs, location, donations, volunteering, and more!" }]);
    }
    setIsOpen(!isOpen);
  };

  const sendMessage = (text) => {
    if (!text.trim()) return;
    setMessages((prev) => [...prev, { from: 'user', text: text.trim() }]);
    setInput('');
    setIsTyping(true);

    setTimeout(() => {
      const response = findBestMatch(text);
      setMessages((prev) => [...prev, {
        from: 'bot',
        text: response || "I'm not sure about that one! For more detailed help, you can:\n\n- Check our FAQ section on the homepage\n- Email us at contact@mcsg.org\n\nIs there something else I can help with?",
      }]);
      setIsTyping(false);
    }, 600 + Math.random() * 400);
  };

  const handleSubmit = (e) => { e.preventDefault(); sendMessage(input); };
  const showQuickReplies = messages.length <= 1;

  return (
    <>
      <div className={`chatbot-window ${isOpen ? 'chatbot-open' : ''}`}>
        <div className="chatbot-header">
          <div className="chatbot-header-info">
            <div className="chatbot-avatar">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C5A55A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>
              </svg>
            </div>
            <div>
              <h4>MCSG Support</h4>
              <span className="chatbot-status"><span className="chatbot-status-dot" />Online</span>
            </div>
          </div>
          <button className="chatbot-close" onClick={toggleChat} aria-label="Close chat"><CloseIcon /></button>
        </div>

        <div className="chatbot-messages">
          {messages.map((msg, i) => (
            <div key={i} className={`chatbot-msg chatbot-msg-${msg.from}`}>
              {msg.from === 'bot' && (
                <div className="chatbot-msg-avatar">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C5A55A" strokeWidth="2"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/></svg>
                </div>
              )}
              <div className="chatbot-msg-bubble">
                {msg.text.split('\n').map((line, j) => (
                  <span key={j}>{line}{j < msg.text.split('\n').length - 1 && <br />}</span>
                ))}
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="chatbot-msg chatbot-msg-bot">
              <div className="chatbot-msg-avatar">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C5A55A" strokeWidth="2"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/></svg>
              </div>
              <div className="chatbot-msg-bubble chatbot-typing">
                <span className="chatbot-typing-dot" /><span className="chatbot-typing-dot" /><span className="chatbot-typing-dot" />
              </div>
            </div>
          )}

          {showQuickReplies && !isTyping && (
            <div className="chatbot-quick-replies">
              {quickReplies.map((text, i) => (
                <button key={i} className="chatbot-quick-btn" onClick={() => sendMessage(text)}>{text}</button>
              ))}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <form className="chatbot-input-area" onSubmit={handleSubmit}>
          <input ref={inputRef} type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type a message..." className="chatbot-input" />
          <button type="submit" className="chatbot-send" disabled={!input.trim()} aria-label="Send message"><SendIcon /></button>
        </form>
      </div>

      <button className={`chatbot-fab ${isOpen ? 'chatbot-fab-active' : ''} ${!hasOpened ? 'chatbot-fab-pulse' : ''}`} onClick={toggleChat} aria-label={isOpen ? 'Close chat' : 'Open chat'}>
        {isOpen ? <CloseIcon /> : <ChatIcon />}
      </button>
    </>
  );
}

export default Chatbot;
